import t from '@motional-cc/fe/tools/translate';
import difference from 'lodash/fp/difference';
import xor from 'lodash/fp/xor';
import isEqual from 'lodash/isEqual';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { userApi } from 'src/api/user';
import { oktaLogout } from 'src/auth/okta';
import LinkButton from 'src/components/common/LinkButton';
import { useMessages } from 'src/contexts/messages';
import { inMilliseconds } from 'src/tools/date-time/inMilliseconds';
import usePrevious from 'src/tools/hooks/usePrevious';
import {
  hasUnpersistedProfileSettings,
  mergeLocalPendingProfileSettings,
} from './mergeProfileSettings';
import { ProfileSettingsContext } from './ProfileSettingsContext';
import { ProfileSettings, SettingSetter } from './types';

const STORAGE_NAME = 'useUserSetting';
// Debounce to batch rapid changes and reduce out-of-order issues
const PERSIST_DEBOUNCE_DELAY = inMilliseconds(5, 'seconds');

const initialSettings: ProfileSettings = JSON.parse(
  localStorage.getItem(STORAGE_NAME) ?? '{}',
);

type Props = {
  children: ReactNode;
};

export function ProfileSettingsProvider({ children }: Props) {
  const { showMessage } = useMessages();
  const { userProfile } = userApi.useUserProfile();
  const isUsingPersistedSettings = useRef(false);
  const previousScopes = usePrevious(userProfile?.scopes);
  const persistTimeoutId = useRef<ReturnType<typeof setTimeout> | undefined>();
  const pendingSettingsRef = useRef<ProfileSettings | undefined>();

  const {
    mutate: updateUserProfile,
    isLoading,
    error,
  } = userApi.useMutateUserProfile({
    successStyle: 'succeed-silently',
    failureStyle: 'fail-silently',
  });
  const [cachedSettings, setCachedSettings] =
    useState<ProfileSettings>(initialSettings);

  const writeSettingsToStorage = useCallback((settings: ProfileSettings) => {
    try {
      localStorage.setItem(STORAGE_NAME, JSON.stringify(settings));
    } catch (_error) {
      // meh, just fail
    }
  }, []);

  const persistSettings = useCallback(
    (settings: ProfileSettings) => {
      updateUserProfile?.(
        'update profile settings',
        {
          settings,
        },
        { ALLOW_SETTINGS_SAVE: true },
      );
    },
    [updateUserProfile],
  );

  const schedulePersistSettings = useCallback(
    (settings: ProfileSettings) => {
      pendingSettingsRef.current = settings;
      clearTimeout(persistTimeoutId.current);
      persistTimeoutId.current = setTimeout(() => {
        persistTimeoutId.current = undefined;
        pendingSettingsRef.current = undefined;
        persistSettings(settings);
      }, PERSIST_DEBOUNCE_DELAY);
    },
    [persistSettings],
  );

  const setSetting = useCallback<SettingSetter>(
    (settingName, updateFunction) => {
      setCachedSettings((cachedSettings) => {
        const currentSettingValue = cachedSettings[settingName];
        const updatedSetting = updateFunction(currentSettingValue);
        const updatedSettings = {
          ...cachedSettings,
        };
        if (updatedSetting === undefined) {
          delete updatedSettings[settingName];
        } else {
          updatedSettings[settingName] = updatedSetting;
        }

        writeSettingsToStorage(updatedSettings);

        if (userProfile) {
          schedulePersistSettings(updatedSettings);
        }

        return updatedSettings;
      });
    },
    [schedulePersistSettings, userProfile, writeSettingsToStorage],
  );

  useEffect(
    function syncFromBackend() {
      if (!userProfile || persistTimeoutId.current) return;

      const backendSettings = userProfile.settings || {};

      if (
        isUsingPersistedSettings.current &&
        isEqual(cachedSettings, backendSettings)
      ) {
        return;
      }

      setCachedSettings((localSettings) => {
        const mergedSettings = mergeLocalPendingProfileSettings(
          backendSettings,
          localSettings,
        );

        isUsingPersistedSettings.current = true;
        writeSettingsToStorage(mergedSettings);

        if (hasUnpersistedProfileSettings(backendSettings, localSettings)) {
          schedulePersistSettings(mergedSettings);
        }

        return mergedSettings;
      });
    },
    [
      cachedSettings,
      schedulePersistSettings,
      userProfile,
      userProfile?.settings,
      writeSettingsToStorage,
    ],
  );

  useEffect(
    function flushPendingSettingsOnPageHide() {
      function flushPendingSettings() {
        if (!persistTimeoutId.current || !pendingSettingsRef.current) return;

        const settings = pendingSettingsRef.current;
        clearTimeout(persistTimeoutId.current);
        persistTimeoutId.current = undefined;
        pendingSettingsRef.current = undefined;
        persistSettings(settings);
      }

      window.addEventListener('pagehide', flushPendingSettings);

      return () => {
        window.removeEventListener('pagehide', flushPendingSettings);
      };
    },
    [persistSettings],
  );

  useEffect(
    function notifyIfScopesChange() {
      const currentScopes = userProfile?.scopes ?? [];

      if (!previousScopes || xor(previousScopes, currentScopes).length === 0)
        return;

      const addedScopes = difference(currentScopes ?? [], previousScopes);
      const removedScopes = difference(previousScopes, currentScopes ?? []);

      showMessage({
        type: 'warning',
        title: 'Your scopes have been updated',
        description: (
          <>
            <p>Your scopes have been updated</p>

            {addedScopes.length > 0 && (
              <p>
                <b>New scopes:</b>{' '}
                {addedScopes
                  .map((scope) => t(`permissions.scopeName.${scope}`))
                  .join(', ')}
              </p>
            )}

            {removedScopes.length > 0 && (
              <p>
                <b>Removed scopes:</b>{' '}
                {removedScopes
                  .map((scope) => t(`permissions.scopeName.${scope}`))
                  .join(', ')}
              </p>
            )}

            <p>
              This may require you to{' '}
              <LinkButton onClick={oktaLogout}>log out</LinkButton> and in again
              to take full effect.
            </p>
          </>
        ),
      });
    },
    [previousScopes, userProfile, showMessage],
  );

  return (
    <ProfileSettingsContext.Provider
      value={{
        settings: cachedSettings,
        setSetting,
        isSaving: isLoading,
        error,
      }}
    >
      {children}
    </ProfileSettingsContext.Provider>
  );
}
